import type { SupportedUiLang } from './localePreference';
import {
  normalizeUiLang,
  persistAutoLanguage,
  persistManualLanguage,
  readManualLanguage,
} from './localePreference';
import i18n from '../i18n';
import { inferRegionalLangFromTimeZone } from './timezoneLocale';

function readLangQueryParam(): SupportedUiLang | null {
  try {
    const raw = new URLSearchParams(window.location.search).get('lang');
    if (!raw) return null;
    return normalizeUiLang(raw.trim());
  } catch {
    return null;
  }
}

/**
 * Browser explicitly requests Swahili or French (any region).
 * Plain English / unknown → ambiguous → IP/country first; timezone only if geo fails (VPN-safe).
 */
function classifyNavigatorLanguages(): 'sw' | 'fr' | 'ambiguous' {
  if (typeof navigator === 'undefined') return 'ambiguous';
  const candidates = [...(navigator.languages || []), navigator.language];
  for (const raw of candidates) {
    const tag = String(raw || '').toLowerCase().replace(/_/g, '-');
    if (tag.startsWith('sw')) return 'sw';
    if (tag.startsWith('fr')) return 'fr';
  }
  return 'ambiguous';
}

/** All navigator languages are English variants (common with VPN + en-US devices). */
function navigatorLanguagesEffectivelyEnglishOnly(): boolean {
  if (typeof navigator === 'undefined') return false;
  const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
  if (candidates.length === 0) return false;
  return candidates.every((raw) => {
    const tag = String(raw).toLowerCase().replace(/_/g, '-');
    return tag.startsWith('en');
  });
}

const GEO_PRIMARY_MS = 750;
const GEO_RETRY_MS = 650;

async function applyLocaleHint(signal: AbortSignal): Promise<boolean> {
  const tryFetch = async (path: string): Promise<{ suggestedLng?: string } | null> => {
    const res = await fetch(path, {
      credentials: 'same-origin',
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as { suggestedLng?: string };
  };

  // Mazuu path first (aligned with mazuufaida.com); fall back to legacy Tcards URL.
  const data =
    (await tryFetch('/api/geo/locale-hint')) ?? (await tryFetch('/api/public/locale-hint'));
  if (!data) return false;
  const lng = normalizeUiLang(data.suggestedLng);
  if (readManualLanguage()) return true;
  await i18n.changeLanguage(lng);
  persistAutoLanguage(lng);
  return true;
}

/**
 * IP/country runs first (matches Mazuu: geo drives locale; timezone is a fallback).
 * If geo fails: English-only browsers → English (fixes CA VPN + African device timezone),
 * else timezone hint for East Africa / French regions.
 */
function scheduleGeoLocaleHint(): void {
  void (async () => {
    const run = async (timeoutMs: number) => {
      const ctrl = new AbortController();
      const tid = window.setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        return await applyLocaleHint(ctrl.signal);
      } catch {
        return false;
      } finally {
        window.clearTimeout(tid);
      }
    };

    let ok = await run(GEO_PRIMARY_MS);
    if (!ok && !readManualLanguage()) {
      await new Promise((r) => setTimeout(r, 400));
      ok = await run(GEO_RETRY_MS);
    }

    if (readManualLanguage()) return;
    if (ok) return;

    if (navigatorLanguagesEffectivelyEnglishOnly()) {
      await i18n.changeLanguage('en');
      persistAutoLanguage('en');
      return;
    }

    const tzLang = inferRegionalLangFromTimeZone();
    if (tzLang === 'sw' || tzLang === 'fr') {
      await i18n.changeLanguage(tzLang);
      persistAutoLanguage(tzLang);
    }
  })();
}

/**
 * Priority (aligned with Mazuu Faida public site):
 * 1. ?lang=en|sw|fr → manual + persist
 * 2. tcards_lang_manual
 * 3. navigator sw/fr → auto + geo refine (IP may adjust)
 * 4. GET /api/geo/locale-hint (Mazuu path; fallback /api/public/locale-hint); retry; then fallback only if geo fails
 */
export async function bootstrapLocale(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const q = readLangQueryParam();
    if (q) {
      persistManualLanguage(q);
      await i18n.changeLanguage(q);
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.has('lang')) {
          u.searchParams.delete('lang');
          const qs = u.searchParams.toString();
          window.history.replaceState({}, '', `${u.pathname}${qs ? `?${qs}` : ''}${u.hash}`);
        }
      } catch {
        /* ignore */
      }
      return;
    }

    if (readManualLanguage()) {
      await i18n.changeLanguage(normalizeUiLang(readManualLanguage()!));
      return;
    }

    const fromNav = classifyNavigatorLanguages();
    if (fromNav === 'sw' || fromNav === 'fr') {
      await i18n.changeLanguage(fromNav);
      persistAutoLanguage(fromNav);
      scheduleGeoLocaleHint();
      return;
    }

    scheduleGeoLocaleHint();
  } catch {
    /* ignore */
  }
}

/** Only devtools / tests */
export function clearLocalePreferenceForTesting(): void {
  try {
    localStorage.removeItem('tcards_lang');
    localStorage.removeItem('tcards_lang_manual');
    localStorage.removeItem('tcards_lang_auto');
  } catch {
    /* ignore */
  }
}
